import {UUID} from '../../../shared/types/uuid.type';
import {Injectable} from '@angular/core';
import {environment} from '../../../../environments/environment';
import {loadUpdatedGridSuccess, updateVisibility} from '../../../store/grids/actions/grid.actions';
import {Store} from '@ngrx/store';
import {io, Socket} from 'socket.io-client';
import {ManagerOptions} from 'socket.io-client/build/esm/manager';
import {SocketOptions} from 'socket.io-client/build/esm/socket';
import {WebSocketConnectionsService} from './web-socket-connections.service';
import {firstValueFrom, Observable} from 'rxjs';
import {MultiviewSubscription, UnregisteredWebSocketEndpoint, WebSocketAction, WebSocketSubscription} from './web-socket.model';

@Injectable({
  providedIn: 'root'
})
export class WebSocketsUnregisteredService {
  private socket: Socket | undefined = undefined;
  private endpoints: UnregisteredWebSocketEndpoint[] = [];
  private multiviewId: UUID | undefined = undefined;

  constructor(
    private readonly store: Store,
    private readonly webSocketConnectionsService: WebSocketConnectionsService
  ) {
    this.initialize();
  }

  private get connectionUrl(): string {
    const baseUrl: string = environment.baseUrl
      .replace('/v1', '')
      .replace('https', 'wss');
    return baseUrl + '/unregistered';
  }

  public subscribe(endpoints: UnregisteredWebSocketEndpoint[]): void {
    for (const endpoint of endpoints) {
      this.subscribeToEndpoint(endpoint);
    }
  }

  public unsubscribe(endpoints: UnregisteredWebSocketEndpoint[]): void {
    for (const endpoint of endpoints) {
      this.unsubscribeFromEndpoint(endpoint);
    }
  }

  public connect(): void {
    this.socket?.connect();
  }

  public disconnect(): void {
    this.socket?.disconnect();
  }

  public updateGrid(res): void {
    this.store.dispatch(loadUpdatedGridSuccess({data: res}));
  }

  public subscribeToMultiview(multiviewId: UUID): void {
    this.multiviewId = multiviewId;
    this.emitMultiviewSubscription();
  }

  public unsubscribeFromMultiview(): void {
    const multiviewId: UUID | undefined = this.multiviewId;
    this.multiviewId = undefined;

    if (multiviewId) {
      this.emitMultiviewAction(multiviewId, 'unsubscribe');
    }
  }

  private subscribeToEndpoint(endpoint: UnregisteredWebSocketEndpoint): void {
    const isSubscribed: boolean = this.endpoints.includes(endpoint);
    if (!isSubscribed) {
      this.endpoints.push(endpoint);
      this.emitAction(endpoint, 'subscribe');
    }
  }

  private unsubscribeFromEndpoint(endpoint: UnregisteredWebSocketEndpoint): void {
    const isSubscribed: boolean = this.endpoints.includes(endpoint);
    if (isSubscribed) {
      this.endpoints = this.endpoints
        .filter((item: UnregisteredWebSocketEndpoint) => item !== endpoint);
      this.emitAction(endpoint, 'unsubscribe');
    }
  }

  private emitMultiviewSubscription(): void {
    if (!this.multiviewId || !this.socket?.connected) {
      return;
    }

    const connectionHook: Observable<void> = this.webSocketConnectionsService.onConnected(this.socket.id);
    firstValueFrom(connectionHook)
      .catch((error: unknown) => console.error('Connection hook execution error:', error));

    this.emitMultiviewAction(this.multiviewId, 'subscribe');
  }

  private emitMultiviewAction(multiviewId: UUID, action: WebSocketAction): void {
    if (!this.socket?.connected) {
      return;
    }

    const payload: MultiviewSubscription = {payload: {action: action, id: multiviewId}};
    this.emit('grid', payload);
  }

  private emitAction(endpoint: UnregisteredWebSocketEndpoint, action: WebSocketAction): void {
    if (!this.socket?.connected) {
      return;
    }

    const payload: WebSocketSubscription = {payload: {action: action}};
    this.emit(endpoint, payload);
  }

  private emit(endpoint: UnregisteredWebSocketEndpoint, message: Record<string, any>): void {
    this.socket.emit(endpoint, message);
  }

  private refreshGrid(): void {
    this.store.dispatch(updateVisibility());
  }

  private initialize(): void {
    const host: string = this.connectionUrl;
    const clientOptions: Partial<ManagerOptions & SocketOptions> = {
      forceNew: true,
      rememberUpgrade: true,
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity
    };

    this.socket = io(host, clientOptions);

    this.socket.on('connect', () => this.onConnected());
    this.socket.on('disconnect', () => this.onDisconnected());
    this.socket.on('grid', (data) => this.updateGrid(data));
    this.socket.on('grid_refresh', () => this.refreshGrid());
    this.socket.on('connect_error', (error: unknown): void => this.onError(error));
  }

  private onConnected(): void {
    console.info('WebSockets Connected');

    for (const endpoint of this.endpoints) {
      this.emitAction(endpoint, 'subscribe');
    }

    this.emitMultiviewSubscription();
  }

  private onDisconnected(): void {
    console.info('WebSockets Disconnected');
  }

  private onError(error: unknown): void {
    console.error('WebSockets Connection error:', error);
  }
}
